import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import { MapboxApi } from '@/common/apis/MapboxApi';
import * as WeatherTargetingEnumerations from '@/common/components/controls/targeting/weather/WeatherTargetingEnumerations';
import AuditTrailItem from '@/common/models/audit/AuditTrailItem';
import Creative from '@/common/models/creative/Creative';
import LineItem from '@/common/models/lineitem/LineItem';
import MediaOwnerApproval from '@/common/models/media-owner/MediaOwnerApproval';
import MobileLineItem from '@/common/models/mobilelineitem/MobileLineItem';
import Package from '@/common/models/package/Package';
import { celsiusToFahrenheit, DASH, getDaypartDescription } from '@/globals';
import LocalCache from '@/utils/LocalCache';
import formatNumber from '@/utils/NumberFormatter';
import i18n from '@/vueI18n';

export default class AuditTrailItemChange {
  entity;

  entityName: string;

  property: string;

  newValue;

  oldValue;

  mapboxApi: MapboxApi;

  constructor(auditTrailItem: AuditTrailItem, property: string, newValue, oldValue) {
    this.entity = auditTrailItem.entity;
    this.entityName = auditTrailItem.entityName;
    this.property = property;
    this.newValue = newValue;
    this.oldValue = oldValue;
    this.mapboxApi = MapboxApi.getInstance();
  }

  get newValueFormatted(): string {
    return this.format(this.newValue);
  }

  get oldValueFormatted(): string {
    return this.format(this.oldValue);
  }

  static getDifferencesDefinitionJSON(val, oldJSON, newJSON) {
    const differences = {};
    Object.entries(oldJSON).forEach(([key]) => {
      if (JSON.stringify(oldJSON[key]) !== JSON.stringify(newJSON[key])) {
        differences[key] = val[key];
      }
    });
    return differences;
  }

  format(val): string {
    if (val === null || val === undefined) {
      return DASH;
    }

    switch (this.entity.code) {
      case 'POLLEN_CATEGORY':
        return val
          .split(',')
          .map((x) => i18n.global.t(`options.pollen.${x.toLowerCase()}`))
          .join(', ');
      case 'WEATHER':
        return val
          .split(',')
          .map((x) => i18n.global.t(`options.weather.${x.toLowerCase()}`))
          .join(', ');
      case 'CURRENT_ABOVE':
      case 'CURRENT_BELOW':
      case 'DAILY_MAX_ABOVE':
      case 'DAILY_MAX_BELOW':
      case 'DAILY_MIN_ABOVE':
      case 'DAILY_MIN_BELOW': {
        let targetTemperature = `${val}°C`;
        if (LocalCache.get('temperatureUnit') === 'F') {
          targetTemperature = `${celsiusToFahrenheit(val)}°F`;
        }
        const fragments = this.entity.code.split('_');
        const allButLastFragments = fragments.slice(0, fragments.length - 1);
        const lastFragment = fragments[fragments.length - 1];
        const limitId = allButLastFragments.join('_');
        const limit = WeatherTargetingEnumerations.allTemperatureLimits().find((x) => x.id === limitId);
        const comparator = WeatherTargetingEnumerations.allComparators().find((x) => x.id === lastFragment);
        return `${limit.name} ${comparator.name} ${targetTemperature}`;
      }
      default:
        break;
    }

    switch (val.constructor) {
      case String: {
        switch (this.property) {
          case 'daypart':
          case 'operating_hours':
            return getDaypartDescription(val);
          case 'state': {
            switch (this.entityName) {
              case 'LineItem': {
                const lineItem = LineItem.fromApi(this.entity);
                lineItem.state = val;
                return capitalize(i18n.global.t(`states.${lineItem.auditStatus}`));
              }
              case 'MobileLineItem': {
                const mobileLineItem = MobileLineItem.fromApi(this.entity);
                mobileLineItem.state = val;
                return capitalize(i18n.global.t(`states.${mobileLineItem.status}`));
              }
              case 'CreativeMediaOwnerApproval': {
                const approval = MediaOwnerApproval.fromApi(this.entity);
                approval.state = val;
                return capitalize(approval.status);
              }
              case 'LineItemMediaOwnerApproval': {
                const approval = MediaOwnerApproval.fromApi(this.entity);
                approval.state = val;
                return capitalize(approval.statusForLineItemAudit);
              }
              case 'LineItemMediaOwner': {
                // const approval = LineItemMediaOwner.fromApi(this.entity);
                // approval.state = val;
                return `${this.entity}`;
              }
              case 'Creative': {
                const creative = Creative.fromApi(this.entity);
                creative.state = val;
                return capitalize(i18n.global.t(`states.${creative.status}`));
              }
              case 'Package': {
                const deal = Package.fromApi(this.entity);
                deal.state = val;
                return capitalize(i18n.global.t(`states.${deal.status}`));
              }
              default:
                break;
            }
            break;
          }
          case 'run_state': {
            switch (this.entityName) {
              case 'LineItem': {
                return val === 'S'
                  ? capitalize(i18n.global.t('states.paused'))
                  : capitalize(i18n.global.t('states.running'));
              }
              case 'Creative': {
                return val === 'S'
                  ? capitalize(i18n.global.t('states.paused'))
                  : capitalize(i18n.global.t('states.running'));
              }
              default:
                break;
            }
            break;
          }
          case 'flight_from':
          case 'flight_to':
          case 'start_date':
          case 'end_date':
            return DateTime.fromISO(val).toLocaleString(DateTime.DATE_SHORT);
          default:
            break;
        }
        if (
          Number.isNaN(Date.parse(val)) ||
          (val.indexOf('/') === -1 && val.indexOf('-') === -1) ||
          this.property.includes('id') ||
          this.property.includes('name') ||
          this.property.includes('notes')
        ) {
          return val;
        }

        const date = DateTime.fromISO(val, { zone: 'utc' }).toLocal();
        if (date.hour === 0 && date.minute === 0 && date.second === 0) {
          return date.toLocaleString(DateTime.DATE_SHORT);
        }
        return date.toLocaleString(DateTime.DATETIME_SHORT);
      }
      case Number: {
        if (this.property.includes('id')) {
          return val.toString();
        }
        return val % 1 !== 0 ? formatNumber(val, '0,0.00') : formatNumber(val, '0,0');
      }
      case Array: {
        return val.join(', ');
      }
      case Object: {
        switch (this.property) {
          case 'geo_json':
            if (!val) break;
            return `<img src="${this.mapboxApi.getStaticMapUrlForGeojson(val)}" alt="map">`;
          case 'definition_json': {
            const differences = AuditTrailItemChange.getDifferencesDefinitionJSON(val, this.oldValue, this.newValue);
            return JSON.stringify(differences, null, ' ');
          }
          default:
            break;
        }
        break;
      }
      default:
        break;
    }

    return `${val}`;
  }
}
